import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import { 
  MODULE_PERMISSIONS, 
  hasModulePermission as checkModulePermission,
  hasAnyModulePermission as checkAnyModulePermission,
  hasWritePermission as checkWritePermission,
  hasReadPermission as checkReadPermission,
  getPermissionStatus as checkPermissionStatus,
  getAvailableChannels as getChannels
} from '../utils/modulePermissions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user } = response.data;


      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      return response;
    } catch (error) {
      // Re-throw the error so it can be caught in the Login component
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const hasRole = (...roles) => {
    return user && roles.includes(user.role);
  };

  const isAdmin = () => hasRole('ADMIN');
  const isStaff = () => hasRole('ADMIN', 'STAFF');

  // Module permission helpers
  const hasModulePermission = (module) => checkModulePermission(user, module);
  const hasAnyModulePermission = (modules) => checkAnyModulePermission(user, modules);
  const getAvailableChannels = () => getChannels(user);
  
  // RBAC permission helpers
  const hasWritePermission = (module) => checkWritePermission(user, module);
  const hasReadPermission = (module) => checkReadPermission(user, module);
  const getPermissionStatus = (module) => checkPermissionStatus(user, module);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      hasRole,
      isAdmin,
      isStaff,
      isAuthenticated: !!user,
      // Module permission methods
      hasModulePermission,
      hasAnyModulePermission,
      getAvailableChannels,
      // RBAC permission methods
      hasWritePermission,
      hasReadPermission,
      getPermissionStatus,
      MODULE_PERMISSIONS
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
